import {
    CopyButton,
    JSONTreeView,
    XMLPrettyView,
} from "@/components/json/JSONTreeView";
import CircularLoader from "@/components/loading/LoadingComponent";
import { fetchWithCSRF } from "@/helpers/common/csrf";
import clsx from "clsx";
import { useEffect, useState } from "react";
import {
    FiArrowLeft,
    FiChevronDown,
    FiChevronUp,
    FiCode,
    FiList,
    FiTrash2,
} from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";

interface WebhookDetail {
    id: string;
    method: string;
    path: string;
    query_string: string;
    headers: Record<string, string>;
    body: string;
    body_json: Record<string, unknown> | null;
    content_type: string;
    source_ip: string | null;
    received_at: string;
}

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;

const methodColors: Record<string, string> = {
    GET: "bg-green-500",
    POST: "bg-blue-500",
    PUT: "bg-amber-500",
    PATCH: "bg-orange-500",
    DELETE: "bg-red-500",
    HEAD: "bg-purple-500",
    OPTIONS: "bg-slate-500",
};

export default function WebhookDetailPage() {
    const { webhook_id } = useParams<{ webhook_id: string }>();
    const navigate = useNavigate();
    const [webhook, setWebhook] = useState<WebhookDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"body" | "headers">("body");
    const [viewMode, setViewMode] = useState<"tree" | "raw">("tree");
    const [headersExpanded, setHeadersExpanded] = useState(true);

    useEffect(() => {
        const fetchWebhook = async () => {
            try {
                const response = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/webhooks/${webhook_id}`,
                );
                if (!response.ok) {
                    throw new Error("Webhook not found");
                }
                const data = await response.json();
                setWebhook(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load");
            } finally {
                setLoading(false);
            }
        };

        if (webhook_id) {
            fetchWebhook();
        }
    }, [webhook_id]);

    const handleDelete = async () => {
        if (window.confirm("Are you sure you want to delete this webhook?")) {
            try {
                await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/webhooks/${webhook_id}`,
                    { method: "DELETE" },
                );
                navigate("/longhouse/webhooks");
            } catch (err) {
                console.error("Error deleting webhook:", err);
            }
        }
    };

    const isXml = (content: string) => {
        return (
            content.trim().startsWith("<?xml") ||
            (content.trim().startsWith("<") && content.trim().endsWith(">"))
        );
    };

    // Check if content supports tree view (JSON or XML)
    const isTreeViewable = (
        body: string | null,
        bodyJson: unknown,
    ): boolean => {
        if (bodyJson) return true;
        if (body && isXml(body)) return true;
        return false;
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <CircularLoader />
            </div>
        );
    }

    if (error || !webhook) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-red-300 bg-red-50 p-6 dark:border-red-700 dark:bg-red-900/20">
                    <h1 className="text-xl font-bold text-red-600 dark:text-red-400">
                        Error
                    </h1>
                    <p className="mt-2 text-slate-600 dark:text-slate-400">
                        {error || "Webhook not found"}
                    </p>
                    <Link
                        to="/longhouse/webhooks"
                        className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:underline dark:text-blue-400"
                    >
                        <FiArrowLeft className="h-4 w-4" />
                        Back to Webhooks
                    </Link>
                </div>
            </div>
        );
    }

    const receivedDate = new Date(webhook.received_at);

    return (
        <div className="mx-auto mt-4 w-full px-4">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link
                        to="/longhouse/webhooks"
                        className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                    >
                        <FiArrowLeft className="h-5 w-5" />
                        Back
                    </Link>
                    <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        Webhook Detail
                    </h1>
                </div>
                <button
                    onClick={handleDelete}
                    className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                >
                    <FiTrash2 className="h-4 w-4" />
                    Delete
                </button>
            </div>

            {/* Overview Card */}
            <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Request Overview
                </h3>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase dark:text-slate-400">
                            Method
                        </label>
                        <div className="mt-1">
                            <span
                                className={clsx(
                                    "rounded-md px-3 py-1 text-sm font-semibold text-white",
                                    methodColors[webhook.method] ||
                                        "bg-slate-500",
                                )}
                            >
                                {webhook.method}
                            </span>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase dark:text-slate-400">
                            Path
                        </label>
                        <p className="mt-1 font-mono text-sm text-slate-900 dark:text-slate-100">
                            {webhook.path}
                        </p>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase dark:text-slate-400">
                            Source IP
                        </label>
                        <p className="mt-1 font-mono text-sm text-slate-700 dark:text-slate-300">
                            {webhook.source_ip || "-"}
                        </p>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase dark:text-slate-400">
                            Received At
                        </label>
                        <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                            {receivedDate.toLocaleString()}
                        </p>
                    </div>
                </div>

                {webhook.query_string && (
                    <div className="mt-6">
                        <label className="text-xs font-semibold text-slate-500 uppercase dark:text-slate-400">
                            Query String
                        </label>
                        <div className="mt-1 flex items-center gap-2">
                            <code className="rounded-lg bg-slate-100 px-3 py-1.5 font-mono text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                ?{webhook.query_string}
                            </code>
                            <CopyButton text={`?${webhook.query_string}`} />
                        </div>
                    </div>
                )}

                <div className="mt-4">
                    <label className="text-xs font-semibold text-slate-500 uppercase dark:text-slate-400">
                        Content Type
                    </label>
                    <p className="mt-1 font-mono text-sm text-slate-700 dark:text-slate-300">
                        {webhook.content_type || "-"}
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-4 flex items-center gap-4 border-b border-slate-200 dark:border-slate-700">
                <button
                    onClick={() => setActiveTab("body")}
                    className={clsx(
                        "border-b-2 px-4 py-3 text-sm font-semibold transition-colors",
                        activeTab === "body"
                            ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                            : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300",
                    )}
                >
                    Body
                </button>
                <button
                    onClick={() => setActiveTab("headers")}
                    className={clsx(
                        "border-b-2 px-4 py-3 text-sm font-semibold transition-colors",
                        activeTab === "headers"
                            ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                            : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300",
                    )}
                >
                    Headers ({Object.keys(webhook.headers).length})
                </button>
            </div>

            {/* Tab Content */}
            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                {activeTab === "body" && (
                    <div>
                        {/* View Mode Toggle and Copy Button */}
                        <div className="mb-4 flex items-center justify-between">
                            {isTreeViewable(webhook.body, webhook.body_json) ? (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setViewMode("tree")}
                                        className={clsx(
                                            "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                                            viewMode === "tree"
                                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                                                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700",
                                        )}
                                    >
                                        <FiList className="h-4 w-4" />
                                        Tree
                                    </button>
                                    <button
                                        onClick={() => setViewMode("raw")}
                                        className={clsx(
                                            "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                                            viewMode === "raw"
                                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                                                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700",
                                        )}
                                    >
                                        <FiCode className="h-4 w-4" />
                                        Raw
                                    </button>
                                </div>
                            ) : (
                                <div />
                            )}
                            {webhook.body && (
                                <CopyButton
                                    text={
                                        webhook.body_json
                                            ? JSON.stringify(
                                                  webhook.body_json,
                                                  null,
                                                  2,
                                              )
                                            : webhook.body
                                    }
                                />
                            )}
                        </div>

                        {/* Body Content */}
                        {webhook.body_json && viewMode === "tree" ? (
                            <JSONTreeView
                                data={webhook.body_json}
                                defaultExpanded={true}
                            />
                        ) : webhook.body &&
                          isXml(webhook.body) &&
                          viewMode === "tree" ? (
                            <XMLPrettyView xml={webhook.body} />
                        ) : webhook.body ? (
                            <pre className="overflow-auto rounded-lg bg-slate-50 p-4 font-mono text-sm whitespace-pre-wrap text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                {webhook.body_json
                                    ? JSON.stringify(webhook.body_json, null, 2)
                                    : webhook.body}
                            </pre>
                        ) : (
                            <p className="text-slate-500 italic dark:text-slate-400">
                                (No body content)
                            </p>
                        )}
                    </div>
                )}

                {activeTab === "headers" && (
                    <div>
                        <div className="mb-4 flex items-center justify-between">
                            <button
                                onClick={() =>
                                    setHeadersExpanded(!headersExpanded)
                                }
                                className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                            >
                                {headersExpanded ? (
                                    <FiChevronUp className="h-4 w-4" />
                                ) : (
                                    <FiChevronDown className="h-4 w-4" />
                                )}
                                {headersExpanded
                                    ? "Collapse All"
                                    : "Expand All"}
                            </button>
                            <CopyButton
                                text={JSON.stringify(webhook.headers, null, 2)}
                            />
                        </div>
                        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                            <table className="w-full">
                                <thead className="bg-slate-50 dark:bg-slate-800">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase dark:text-slate-400">
                                            Header
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase dark:text-slate-400">
                                            Value
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                    {Object.entries(webhook.headers).map(
                                        ([key, value]) => (
                                            <tr
                                                key={key}
                                                className="hover:bg-slate-50 dark:hover:bg-slate-800"
                                            >
                                                <td className="px-4 py-3 font-mono text-sm text-blue-600 dark:text-blue-400">
                                                    {key}
                                                </td>
                                                <td className="px-4 py-3 font-mono text-sm break-all text-slate-700 dark:text-slate-300">
                                                    {value}
                                                </td>
                                            </tr>
                                        ),
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
